import {DestroyRef, Directive, effect, inject, signal} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable} from 'rxjs';
import {FormMode} from '../../shared/types/common';
import {UtilService} from '../../services/util.service';
import {toEntityKey} from '../../utils/strings';
import {ConfirmTypeToDeleteModalComponent} from '../../shared/components/confirm-type-to-delete-modal/confirm-type-to-delete-modal.component';

export interface ConfirmDeleteConfig {
  header: string;
  description: string;
  confirmLabel: string;
  expectedValue: string;
}

/**
 * Shared scaffolding for the management-screen form drawers (account/user/brand). Owns the bits every
 * drawer repeated verbatim: the `saving` flag (and the disable-close lock it drives), the entity +
 * close callback handed in through the drawer data, the snackbar success/error handlers, and the
 * type-to-confirm delete flow.
 *
 * Each drawer keeps its own form, configs and submit() — those legitimately differ per entity (the
 * brand drawer's submit runs an extra domain-conflict check first), so they are intentionally NOT
 * pulled up here. Subclasses must call super() and provide the form themselves.
 */
@Directive()
export abstract class DrawerFormBase<E> {
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly utilService = inject(UtilService);
  protected readonly dialog = inject(MatDialog);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly data = inject(MAT_DIALOG_DATA);

  protected readonly FormMode = FormMode;
  public currentMode: FormMode = FormMode.ADD;
  public readonly saving = signal(false);

  // Supplied by the opener (management-screen) via the drawer data: the row being edited (absent in
  // add mode) and a callback that runs the drawer's animated close.
  protected readonly entity = this.data?.data?.entity as E | undefined;
  protected readonly close: () => void = (this.data?.data?.close as () => void) ?? (() => {});

  // A key the changed name now derives, held back because the user hand-picked the current one
  // (DEV-545). Non-null renders the click-to-refresh note under the key field; it is never a form
  // validation error, so a deliberately different key does not block the save.
  public readonly suggestedKey = signal<string | null>(null);

  // Registered by trackGeneratedKey for the drawers whose key is derived from a name.
  private nameControl?: FormControl;
  private keyControl?: FormControl;

  constructor() {
    // Lock the drawer (backdrop-click + Escape) while a save/delete is in flight so it can't be
    // dismissed mid-request; programmatic close() on success/cancel still works.
    effect(() => (this.dialogRef.disableClose = this.saving()));
  }

  cancel(): void {
    this.close();
  }

  protected onSuccess(message: string): void {
    this.utilService.openSnackBar(message, {position: 'center', variant: 'success'});
    this.close();
  }

  protected onError(error: unknown): void {
    this.saving.set(false);
    const message = (error as {error?: {message?: string}})?.error?.message ?? 'Something went wrong. Please try again.';
    this.utilService.openSnackBar(message, {variant: 'error', position: 'center'});
  }

  /**
   * Opts a drawer into name-derived keys (DEV-545); call once from the subclass constructor with its
   * name and key controls. Add mode only — a created entity's key is immutable, and its control is
   * disabled in edit mode, so there is nothing to generate or dismiss.
   *
   * The drawer template then wires `(blur)="onNameBlur()"` on the name input and the click-to-refresh
   * note (see `suggestedKey`) under the key field.
   */
  protected trackGeneratedKey(nameControl: FormControl, keyControl: FormControl): void {
    if (this.entity) {
      return;
    }
    this.nameControl = nameControl;
    this.keyControl = keyControl;
    // The note asks the user to act on the key; any change to the key answers it — whether it came
    // from refreshKey() or from the user typing their own value.
    keyControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.suggestedKey.set(null));
  }

  /**
   * Generates the key from the typed name, on leaving the name field — keys are derived instead of
   * written by hand.
   *
   * An untouched key just follows the name (`dirty` is set by typing into a control but not by
   * `setValue`, so this stays true across our own updates). A key the user hand-picked is never
   * overwritten: the freshly derived key is offered through `suggestedKey` instead, which surfaces the
   * "name was changed" note next to the key field. A name with nothing key-safe in it (e.g. all
   * non-Latin) is ignored rather than blanking a key that is already there.
   */
  onNameBlur(): void {
    if (this.currentMode !== FormMode.ADD || !this.nameControl || !this.keyControl) {
      return;
    }
    const key = toEntityKey(this.nameControl.value ?? '');
    if (!key || key === this.keyControl.value) {
      this.suggestedKey.set(null);
      return;
    }
    if (this.keyControl.dirty && this.keyControl.value) {
      this.suggestedKey.set(key);
      return;
    }
    this.keyControl.setValue(key); // clears any note through the valueChanges above
  }

  /**
   * Applies the offered key — the note's "Click here". Marking the control pristine hands generation
   * back to the name, so from here on the key follows it again until the user edits the key themselves.
   */
  refreshKey(): void {
    const key = this.suggestedKey();
    if (!key || !this.keyControl) {
      return;
    }
    this.keyControl.setValue(key);
    this.keyControl.markAsPristine();
  }

  // Opens the type-to-confirm delete dialog; on confirmation, runs the delete request with the
  // standard saving / success / error handling. Used by the account and user drawers.
  protected confirmAndDelete(config: ConfirmDeleteConfig, request: () => Observable<unknown>, successMessage: string): void {
    const dialogRef = this.dialog.open(ConfirmTypeToDeleteModalComponent, {
      data: config,
      panelClass: 'management-modal',
      autoFocus: false,
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.saving.set(true);
      request()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => this.onSuccess(successMessage),
          error: error => this.onError(error),
        });
    });
  }
}
